"use client"

import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import CategoryModal from "./category-modal"
import { useApp } from "@/lib/context"
import OptimizedImage from "./optimized-image"
import { effectivePrice } from "@/lib/api"
import { ChevronRight, Clock, Utensils } from "lucide-react"
import SectionHeader, { type SectionVariant } from "./section-header"

export type { SectionVariant }

interface CategoriesSectionProps {
  variant?: SectionVariant
}

// Componente de esqueleto para carregamento
const CategorySkeleton = () => (
  <div className="bg-card rounded-xl overflow-hidden shadow-md h-full animate-pulse border border-border">
    <div className="h-32 sm:h-40 md:h-48 bg-muted" />
    <div className="p-3 sm:p-4 bg-card">
      <div className="h-4 sm:h-5 bg-muted rounded w-2/3 mb-2" />
      <div className="h-3 sm:h-4 bg-muted rounded w-1/2 mb-1" />
      <div className="h-3 sm:h-4 bg-muted rounded w-1/3" />
    </div>
  </div>
)

const STYLES: Record<SectionVariant, {
  section: string
  card: string
  overlay: string
  badge: string
  cardTitle: string
  meta: string
  content: string
  fromLabel: string
  price: string
  arrow: string
  progressTrack: string
  progressBar: string
  seeAll: string
}> = {
  default: {
    section: "py-10 sm:py-12 lg:py-16 px-3 sm:px-4 bg-background",
    card: "bg-card text-card-foreground rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group h-full border border-border",
    overlay: "bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-60 group-hover:opacity-80",
    badge: "bg-primary text-primary-foreground",
    cardTitle: "group-hover:text-primary",
    meta: "text-white/90",
    content: "p-3 sm:p-4 bg-card",
    fromLabel: "text-muted-foreground",
    price: "text-primary",
    arrow: "bg-primary text-primary-foreground shadow-md",
    progressTrack: "bg-muted",
    progressBar: "bg-primary",
    seeAll: "bg-primary text-primary-foreground hover:opacity-90 shadow-lg hover:shadow-xl",
  },
  modern: {
    section: "py-10 sm:py-12 lg:py-16 px-3 sm:px-4 bg-transparent",
    card: "bg-white/5 backdrop-blur-md rounded-xl overflow-hidden border border-white/10 hover:border-orange-500/40 hover:shadow-[0_0_50px_-12px_rgba(249,115,22,0.5)] transition-all duration-300 group h-full",
    overlay: "bg-gradient-to-t from-black/80 via-black/30 to-transparent",
    badge: "bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg",
    cardTitle: "group-hover:text-orange-400",
    meta: "text-white/80",
    content: "p-3 sm:p-4 bg-transparent border-t border-white/10",
    fromLabel: "text-white/50",
    price: "text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-300",
    arrow: "bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg",
    progressTrack: "bg-white/10",
    progressBar: "bg-gradient-to-r from-orange-500 to-amber-400",
    seeAll: "bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700 shadow-lg hover:shadow-xl",
  },
  vibrant: {
    section: "py-10 sm:py-12 lg:py-16 px-3 sm:px-4 bg-transparent",
    card: "bg-white rounded-3xl overflow-hidden shadow-xl shadow-purple-100/60 hover:shadow-2xl transition-all duration-300 group h-full border-2 border-transparent hover:border-purple-200",
    overlay: "bg-gradient-to-t from-purple-900/70 via-pink-600/20 to-transparent",
    badge: "bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-md",
    cardTitle: "group-hover:text-pink-500",
    meta: "text-white/90",
    content: "p-3 sm:p-4 bg-gradient-to-br from-purple-50 via-white to-cyan-50",
    fromLabel: "text-gray-500",
    price: "text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500",
    arrow: "bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-md",
    progressTrack: "bg-purple-100",
    progressBar: "bg-gradient-to-r from-purple-500 via-pink-500 to-cyan-400",
    seeAll: "bg-gradient-to-r from-purple-500 via-pink-500 to-cyan-400 text-white hover:opacity-90 shadow-lg hover:shadow-xl",
  },
  minimal: {
    section: "py-10 sm:py-12 lg:py-16 px-3 sm:px-4 bg-transparent",
    card: "bg-white rounded-none overflow-hidden border border-foreground/10 group h-full transition-shadow duration-300 hover:shadow-md",
    overlay: "bg-gradient-to-t from-black/60 via-transparent to-transparent",
    badge: "bg-foreground text-background",
    cardTitle: "group-hover:underline underline-offset-4",
    meta: "text-white",
    content: "p-3 sm:p-4 border-t border-foreground/10",
    fromLabel: "text-neutral-500 uppercase tracking-wider font-mono",
    price: "text-foreground font-mono",
    arrow: "bg-foreground text-background",
    progressTrack: "bg-foreground/10",
    progressBar: "bg-foreground",
    seeAll: "bg-foreground text-background hover:bg-black transition-colors rounded-none",
  },
}

export default function CategoriesSection({ variant = "default" }: CategoriesSectionProps) {
  const { t, categories, isLoading, error } = useApp()
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [visibleCategories, setVisibleCategories] = useState<number>(6)
  const styles = STYLES[variant]

  const handleCategoryClick = (categoryName: string) => {
    setSelectedCategory(categoryName)
  }

  const handleCloseModal = () => {
    setSelectedCategory(null)
  }

  const selectedCategoryData = categories.find((category) => category.nome === selectedCategory)

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("pt-AO", {
      style: "currency",
      currency: "AOA",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price)
  }

  // Carregar mais categorias gradualmente
  useEffect(() => {
    if (categories.length > visibleCategories) {
      const timer = setTimeout(() => {
        setVisibleCategories((prev) => Math.min(prev + 3, categories.length))
      }, 200)
      return () => clearTimeout(timer)
    }
  }, [categories.length, visibleCategories])

  if (error) {
    return (
      <div className="py-8 sm:py-12 px-4 bg-muted">
        <div className="max-w-7xl mx-auto text-center">
          <div className="bg-card rounded-xl p-4 sm:p-6 shadow-lg border border-border">
            <h2 className="text-lg sm:text-2xl font-bold text-destructive mb-2 sm:mb-4">Erro ao carregar o menu</h2>
            <p className="text-sm sm:text-base text-muted-foreground">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Seção Principal do Menu */}
      <section id="categories" className={styles.section}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="max-w-7xl mx-auto"
        >
          {/* Cabeçalho da Secção - Separador com eyebrow + régua */}
          <SectionHeader
            variant={variant}
            eyebrow="Pratos"
            title={t("ourMenu")}
            subtitle="Descubra sabores únicos da culinária angolana com toques contemporâneos."
          />

          {/* Grid de Categorias Responsivo */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
            {isLoading
              ? Array(6)
                  .fill(0)
                  .map((_, index) => <CategorySkeleton key={index} />)
              : categories.slice(0, visibleCategories).map((category, index) => (
                  <motion.div
                    key={category.nome}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="cursor-pointer group touch-manipulation"
                    whileHover={{ y: -4 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleCategoryClick(category.nome)}
                  >
                    <div className={styles.card}>
                      {/* Imagem da Categoria - Altura Responsiva */}
                      <div className="relative h-32 sm:h-40 md:h-48 overflow-hidden">
                        <OptimizedImage
                          src={category.urls[0] || "/placeholder.svg"}
                          alt={category.nome}
                          className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${
                            variant === "minimal" ? "grayscale group-hover:grayscale-0 transition-all" : ""
                          }`}
                          priority={index < 3}
                        />

                        {/* Overlay Gradiente */}
                        <div className={`absolute inset-0 transition-opacity duration-300 ${styles.overlay}`} />

                        {/* Título sobre a imagem - Responsivo */}
                        <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
                          <h3 className={`text-lg sm:text-xl lg:text-2xl font-bold text-white mb-1 transition-colors duration-300 ${styles.cardTitle}`}>
                            {category.nome}
                          </h3>
                          <div className={`flex items-center gap-2 sm:gap-3 text-xs sm:text-sm ${styles.meta}`}>
                            <span className="flex items-center gap-1">
                              <Utensils className="w-3 h-3" />
                              {category.pratos.length}
                            </span>
                            {category.pratos.length > 0 && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {Math.min(...category.pratos.map((dish) => dish.tempo_preparo || 15))} min
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Conteúdo do Card - Compacto */}
                      <div className={styles.content}>
                        <div className="flex justify-between items-center">
                          <div className="flex-1 min-w-0">
                            {category.pratos.length > 0 && (
                              <>
                                <div className={`text-xs mb-1 ${styles.fromLabel}`}>{t("from")}</div>
                                <div className={`text-lg sm:text-xl font-bold truncate ${styles.price}`}>
                                  {formatPrice(Math.min(...category.pratos.map((dish) => effectivePrice(dish))))}
                                </div>
                              </>
                            )}
                          </div>

                          <motion.div
                            className={`w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center flex-shrink-0 ml-2 ${
                              variant === "minimal" ? "" : "rounded-full"
                            } ${styles.arrow}`}
                            whileHover={{ scale: 1.1, rotate: 90 }}
                            transition={{ duration: 0.3 }}
                          >
                            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                          </motion.div>
                        </div>

                        {/* Barra de progresso decorativa */}
                        <div className={`mt-2 sm:mt-3 overflow-hidden ${variant === "minimal" ? "h-0.5" : "h-1 rounded-full"} ${styles.progressTrack}`}>
                          <motion.div
                            className={`h-full ${styles.progressBar}`}
                            initial={{ width: "0%" }}
                            whileInView={{ width: "100%" }}
                            transition={{ duration: 1.2, delay: index * 0.1 }}
                          />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
          </div>

          {/* Botão Ver Mais - Responsivo */}
          {visibleCategories < categories.length && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-center mt-6 sm:mt-8"
            >
              <button
                onClick={() => setVisibleCategories(categories.length)}
                className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold text-sm sm:text-base transition-all duration-300 transform hover:scale-105 touch-manipulation ${styles.seeAll}`}
              >
                Ver Todas as Categorias
              </button>
            </motion.div>
          )}
        </motion.div>
      </section>

      {/* Modal da Categoria */}
      {selectedCategory && selectedCategoryData && (
        <CategoryModal category={selectedCategoryData} onClose={handleCloseModal} variant={variant} />
      )}
    </>
  )
}
